/**
 * The records a review summary lists (client-MCP plan §4.6 stage 5, §4.7 "Batches"; backend P3-16
 * `ai_edit.review_summary`; contract `../apiv2/dashboard_ai_edit.openapi.yaml` `AIProposalPreviewSummary.sections`,
 * `AIReviewSection`, `AIContentRecordSummary`, `AIRevertRootLine`). Shared by the proposal review page
 * (`PreviewSummary`) and the approvals inbox card (`parts.js` `PendingChangeCard`).
 *
 * - **Self-describing.** A summary lists its record `sections`: the key holding the records, the kind in words
 *   (`label`), the field naming a record (`label_field`; `id_field` our id) and the fields holding the company's or its
 *   AI's text (`untrusted_fields`). Everything here is rendered from that description, never per kind, so a content
 *   kind added on the backend shows without a change here. The vocabulary's records keep the AI vocabulary page's words
 *   (`VocabularyRecords`: a binding reads as the regions or roles it names) inside the same frame.
 * - **Untrusted text.** The sections sit in one labelled frame (`UntrustedBlock`, the summary's `legend`), and every
 *   value of an untrusted field renders through `UntrustedText`: plain text, never markup, never a link. Our own words
 *   -- the operation, the kind, the field names, counts, warnings, a redaction note -- stay outside the quoted values.
 * - **Per record.** A record is one aggregate root (one change set once applied): a line with its operation, name, id,
 *   the fields it sets or changes, its warnings (`item_warnings`, by proposal item) and the fields a booking system
 *   manages (`managed_fields`: the next sync overwrites them; the requester's choice). Its diff opens below it: at
 *   once for a small change, on demand when the summary says `per_root_summary` (a large batch is read line by line,
 *   not as a wall of diffs; each diff still opens). A batch revert's lines (`roots`) each name their kind and link their
 *   change set.
 * - **Per viewer.** A franchise licensee's copy of a parent record comes `redacted` for a viewer who does not read the
 *   parent's records: its operation and fields, and the backend's note -- never the parent's name, id or values.
 */
import { Alert, Box, Button, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { UntrustedBlock, UntrustedText } from '../../components/UntrustedText';
import { VocabularyRecords } from '../../settings/aiVocabulary/vocabularyWords';
// parts.js renders this module on its cards; BASE is read at render time only, so the import cycle is harmless.
import { BASE } from './parts';

const isObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const count = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "country_codes" -> "Country codes": a field or kind name in words. */
export const humanize = (key) => {
    const s = String(key ?? '').replace(/_/g, ' ').trim();
    return s ? s[0].toUpperCase() + s.slice(1) : '';
};

/** A value of the company's text as one line: lists of words joined, anything else structured as its JSON text. */
const valueText = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v) && v.every((x) => x === null || ['string', 'number', 'boolean'].includes(typeof x))) {
        return v.filter((x) => x !== null && x !== '').join(', ');
    }
    try {
        return JSON.stringify(v);
    } catch (_e) {
        return String(v);
    }
};

const Value = ({ value }) => <UntrustedText inline text={valueText(value)} empty="(none)" />;

const OP_WORDS = { create: 'New', update: 'Changed', delete: 'Removed' };
const opWord = (op) => (Object.prototype.hasOwnProperty.call(OP_WORDS, op) ? OP_WORDS[op] : humanize(op));

// The record fields that are our own words (the contract's): anything else a section does not name untrusted is
// ignored, never shown raw.
const CONTENT_UNTRUSTED = ['label', 'changes', 'after', 'before'];
const VOCABULARY_SECTION = {
    key: 'vocabulary',
    domain: 'vocabulary',
    kind: 'tenant_term',
    scope: 'vocabulary',
    label: 'AI vocabulary entry',
    id_field: 'entry',
    label_field: 'term',
    untrusted_fields: ['term', 'changes', 'after', 'before', 'binding_names'],
};

/**
 * The record sections of `summary` it can render: those it lists whose key holds a list. An older backend's summary
 * without `sections` (before P3-16) is described as the backend does: a vocabulary summary's `vocabulary[]`.
 */
export function sectionsOf(summary) {
    if (!isObject(summary)) return [];
    if (Array.isArray(summary.sections)) {
        return summary.sections.filter((s) => isObject(s) && typeof s.key === 'string' && Array.isArray(summary[s.key]));
    }
    if (Array.isArray(summary.vocabulary)) return [{ ...VOCABULARY_SECTION, records: summary.vocabulary.length }];
    return [];
}

const recordsOf = (summary, section) => summary[section.key].filter(isObject);

/** `{changes: {field: {before, after}}}`: every value is a before → after pair. */
const isChangesMap = (v) => isObject(v) && Object.keys(v).length > 0
    && Object.values(v).every((c) => isObject(c) && ('before' in c || 'after' in c));

const TABLE_TITLES = { after: 'As it will read', before: 'As it read' };

const cellSx = { verticalAlign: 'top', wordBreak: 'break-word' };

/** One untrusted field of a record: a before → after table, a record's fields, or one value. */
function UntrustedField({ field, value, name }) {
    if (isChangesMap(value)) {
        return (
            <Table size="small" aria-label={`Changes of ${name}`} sx={{ mt: 0.5 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Before</TableCell>
                        <TableCell>After</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(value).map(([key, change]) => (
                        <TableRow key={key}>
                            <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>{humanize(key)}</TableCell>
                            <TableCell sx={cellSx}><Value value={change.before} /></TableCell>
                            <TableCell sx={cellSx}><Value value={change.after} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }
    if (isObject(value)) {
        const title = TABLE_TITLES[field] || humanize(field);
        return (
            <Table size="small" aria-label={`${title}: ${name}`} sx={{ mt: 0.5 }}>
                <TableBody>
                    {Object.entries(value).map(([key, v]) => (
                        <TableRow key={key}>
                            <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', width: '30%' }}>{humanize(key)}</TableCell>
                            <TableCell sx={cellSx}><Value value={v} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }
    return (
        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            {`${TABLE_TITLES[field] || humanize(field)}: `}
            <Value value={value} />
        </Typography>
    );
}

// The fields a booking system manages, the warning the backend words for each, and what the requester chose
// (`change_engine.managed.report`: include = update them anyway, the warning recorded; skip = leave them out).
function ManagedFields({ item, entry }) {
    if (!isObject(entry)) return null;
    const fields = (Array.isArray(entry.fields) ? entry.fields : []).filter(isObject);
    if (!fields.length) return null;
    let choice = 'No choice was recorded for these fields.';
    if (entry.choice === 'include') choice = 'The requester chose to update these fields anyway: the warning is recorded on the change.';
    if (entry.choice === 'skip') {
        const dropped = (Array.isArray(entry.dropped) && entry.dropped.length ? entry.dropped : fields.map((f) => f.field)).map(humanize);
        choice = `The requester chose to leave them out: ${dropped.join(', ')} ${dropped.length === 1 ? 'is' : 'are'} not changed.`;
    }
    return (
        <Alert severity="warning" data-testid={`managed-${item}`} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Fields a booking system manages</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {fields.map((f) => (
                    <Typography key={`${f.field}:${f.system}`} component="li" variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {typeof f.warning === 'string' && f.warning ? f.warning : `${f.field}: managed by ${f.system}`}
                    </Typography>
                ))}
            </Box>
            <Typography variant="body2">{choice}</Typography>
        </Alert>
    );
}

const Warnings = ({ texts, testId }) => (texts.length ? (
    <Alert severity="warning" data-testid={testId} sx={{ mt: 1 }}>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {texts.map((w, i) => (
                // keyed by position: a static list, and two warnings may read alike
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={i} component="li" variant="body2" sx={{ wordBreak: 'break-word' }}>{String(w)}</Typography>
            ))}
        </Box>
    </Alert>
) : null);

const textsOf = (warnings, item) => {
    const texts = isObject(warnings) ? warnings[String(item)] : null;
    return Array.isArray(texts) ? texts.filter((t) => t !== null && t !== undefined && t !== '') : [];
};

/** One record: its line (our words and its name), its warnings and managed fields, and its diff below. */
function RecordView({ summary, section, record, index, perRoot }) {
    const [open, setOpen] = React.useState(!perRoot);
    const labelField = section.label_field || 'label';
    const idField = section.id_field || 'id';
    const untrusted = Array.isArray(section.untrusted_fields) ? section.untrusted_fields : [labelField, ...CONTENT_UNTRUSTED];
    const name = valueText(record[labelField]) || (record.redacted ? 'a record that is not shown' : 'a record without a name');
    const details = untrusted.filter((f) => f !== labelField && record[f] !== undefined && record[f] !== null);
    const fields = (Array.isArray(record.fields) ? record.fields : []).map(humanize).filter(Boolean);
    const warnings = textsOf(summary.item_warnings, record.item);
    const warningCount = count(record.warnings) ?? warnings.length;
    const id = record[idField];
    const kindWord = section.kind ? null : humanize(record.kind);
    const extra = [
        count(record.entries) !== null ? plural(record.entries, 'entry', 'entries') : null,
        typeof record.status === 'string' && record.status ? record.status.replace(/_/g, ' ') : null,
    ].filter(Boolean).join(' · ');
    return (
        <Box data-testid={`record-${section.key}-${index}`} data-record sx={{ py: 1, borderTop: index ? 1 : 0, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip size="small" label={opWord(record.op)} />
                {kindWord && <Typography variant="body2" color="text.secondary">{kindWord}</Typography>}
                <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                    <UntrustedText inline text={valueText(record[labelField])} empty={record.redacted ? '(not shown)' : '(no name)'} />
                </Typography>
                {id !== null && id !== undefined && id !== '' && <Typography variant="caption" color="text.secondary">{`#${id}`}</Typography>}
                {record.copy_of_parent === true && <Chip size="small" variant="outlined" label="Copy of the franchise's" />}
                {warningCount > 0 && <Chip size="small" color="warning" variant="outlined" label={plural(warningCount, 'warning', 'warnings')} />}
                {extra && <Typography variant="caption" color="text.secondary">{extra}</Typography>}
                {typeof record.change_set === 'string' && record.change_set && (
                    <Typography variant="body2" component="span">
                        <RouterLink to={`${BASE}/${record.change_set}`}>Its change set</RouterLink>
                    </Typography>
                )}
                {details.length > 0 && (
                    <Button size="small" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
                        {open ? 'Hide changes' : 'Show changes'}
                    </Button>
                )}
            </Stack>
            {fields.length > 0 && (
                <Typography variant="caption" color="text.secondary" component="div">
                    {`${record.op === 'create' ? 'Sets' : 'Changes'}: ${fields.join(', ')}`}
                </Typography>
            )}
            {record.redacted === true && typeof record.note === 'string' && record.note && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>{record.note}</Typography>
            )}
            {Array.isArray(record.shortened) && record.shortened.length > 0 && (
                <Typography variant="caption" color="text.secondary" component="div">
                    {`Shortened on this page: ${record.shortened.map(humanize).join(', ')}.`}
                </Typography>
            )}
            <Warnings texts={warnings} testId={`warnings-${section.key}-${index}`} />
            <ManagedFields item={record.item} entry={isObject(summary.managed_fields) ? summary.managed_fields[String(record.item)] : null} />
            {open && details.map((f) => <UntrustedField key={f} field={f} value={record[f]} name={name} />)}
        </Box>
    );
}

/** The item ids of the records shown one by one (their warnings show with them; any other's after the sections --
 *  the vocabulary's too: its records keep the AI vocabulary page's words, without warnings). */
const listedItems = (summary, sections) => new Set(sections
    .filter((s) => s.domain !== 'vocabulary')
    .flatMap((s) => recordsOf(summary, s).filter((r) => r.item !== undefined && r.item !== null).map((r) => String(r.item))));

/**
 * The record sections of a review summary in one labelled frame (module docstring). `title`: the frame's ("What it
 * changes", "What it reverts"). Null when the summary lists no records (a notification change).
 */
export function ReviewSections({ summary, title = 'What it changes' }) {
    const sections = sectionsOf(summary);
    if (!sections.length) return null;
    const perRoot = summary.per_root_summary === true;
    const shown = sections.reduce((n, s) => n + recordsOf(summary, s).length, 0);
    const items = count(summary.items);
    const notShown = count(summary.records_not_shown) ?? (items !== null ? Math.max(0, items - shown) : 0);
    const listed = listedItems(summary, sections);
    const others = isObject(summary.item_warnings)
        ? Object.entries(summary.item_warnings).filter(([key]) => !listed.has(key)).flatMap(([, texts]) => (Array.isArray(texts) ? texts : []))
            .filter((t) => t !== null && t !== undefined && t !== '')
        : [];
    const legend = typeof summary.legend === 'string' && summary.legend ? summary.legend : null;
    return (
        <Box data-testid="review-sections" sx={{ mt: 1 }}>
            <UntrustedBlock title={title} legend={legend}>
                {sections.map((section) => {
                    const records = recordsOf(summary, section);
                    return (
                        <Box key={section.key} data-section={section.key} sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" component="div">{`${section.label || humanize(section.key)} (${records.length})`}</Typography>
                            {section.domain === 'vocabulary' ? (
                                <VocabularyRecords records={records} />
                            ) : (
                                records.map((record, index) => (
                                    // keyed by position: the order is the summary's, and a new record has no id yet
                                    // eslint-disable-next-line react/no-array-index-key
                                    <RecordView
                                        key={index}
                                        summary={summary}
                                        section={section}
                                        record={record}
                                        index={index}
                                        perRoot={perRoot}
                                    />
                                ))
                            )}
                        </Box>
                    );
                })}
                {notShown > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {notShown === 1 ? '1 more record is not listed here.' : `${notShown} more records are not listed here.`}
                    </Typography>
                )}
            </UntrustedBlock>
            <Warnings texts={others} testId="review-warnings" />
        </Box>
    );
}
